/*
* Report common utilities & components
*/

import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Row, Table } from 'react-bootstrap';
import { FormattedNumber } from 'react-intl';

import { AppDataConstants } from '../../common/app-data-constants';
import * as AppUtilities from '../../common/apputilities';
import { BootstrapColXl as Col } from '../../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../../common/formatters';
import { getComponentDisplayId, getComponentSortFields } from '../component-sorting';

import { toPercentage, toUSCurrency } from '../../utils/numberUtils';
import { ISpecialAssessmentPresentation } from './special-assessments-presentation';

// FORMATTING FUNCTIONS
export const shortDate = (date) => moment(date).format('M/D/YYYY');
export const monthDate = (date) => moment(date).format('MMM D, YYYY');

// CONSTANTS
export const ReportOverrideDraft = false;

export const ReportSectionNames = {
    componentEvaluation: 'component-evaluation',
    componentFundingInformation: 'component-funding-information',
    executiveSummary: 'executive-summary',
    faq: 'faq',
    fundingSummary: 'funding-summary',
    glossary: 'glossary',
    introduction: 'introduction',
    percentFundedGraph: 'percent-funded-graph',
    projectedReserveExpenditures: 'projected-reserve-expenditures',
    reserveContributionsGraph: 'reserve-contributions-graph',
    specialAssessments: 'special-assessments',
    significantComponents: 'significant-components',
    significantComponentsGraph: 'significant-components-graph',
    tableOfContents: 'table-of-contents',
    yearlyCashFlow: 'yearly-cash-flow',
    yearlyReserveExpendituresGraph: 'yearly-reserve-expenditures-graph',
    yearlySummary: 'yearly-summary',
    yearlySummaryCurrent: 'yearly-summary-current',
    yearlySummaryMinimum: 'yearly-summary-minimum',
    yearlySummaryRecommended: 'yearly-summary'
};

export const ReportSectionTitles = {
    [ReportSectionNames.componentEvaluation]: 'Component Evaluation',
    [ReportSectionNames.componentFundingInformation]: 'Component Funding Information',
    [ReportSectionNames.executiveSummary]: 'Executive Summary',
    [ReportSectionNames.faq]: 'General Information and Frequently Asked Questions',
    [ReportSectionNames.fundingSummary]: 'Funding Summary',
    [ReportSectionNames.glossary]: 'Glossary',
    [ReportSectionNames.introduction]: 'Introduction',
    [ReportSectionNames.percentFundedGraph]: 'Percent Funded - Graph',
    [ReportSectionNames.projectedReserveExpenditures]: 'Projected Reserve Expenditures by Year',
    [ReportSectionNames.reserveContributionsGraph]: 'Reserve Contributions - Graph',
    [ReportSectionNames.specialAssessments]: 'Special Assessment Schedule',
    [ReportSectionNames.significantComponentsGraph]: 'Significant Components - Graph',
    [ReportSectionNames.significantComponents]: 'Significant Components',
    [ReportSectionNames.tableOfContents]: 'Table of Contents',
    [ReportSectionNames.yearlyCashFlow]: 'Yearly Cash Flow',
    [ReportSectionNames.yearlyReserveExpendituresGraph]: 'Yearly Reserve Expenditures - Graph',
    [ReportSectionNames.yearlySummary]: 'Yearly Summary',
    [ReportSectionNames.yearlySummaryCurrent]: 'Yearly Summary (Current Funding)',
    [ReportSectionNames.yearlySummaryMinimum]: 'Yearly Summary (Minimum Funding)',
    [ReportSectionNames.yearlySummaryRecommended]: 'Yearly Summary'
};

export const ReportOrderServiceLevels = {
    level1: 'Level 1',
    level2: 'Level 2',
    level3: 'Level 3',
    threeYearPlan: 'Level 4',
};

export const YearlySummaryTypes = {
    hypoValues: 'hypo-values',
    currentValues: 'current-values',
    minimumValues: 'minimum-values',
    recommendedValues: 'recommended-values'
};

// FORMATTERS
export const Currency = ({ value, decimals = 2, hidden = false, decimalsParse = 4, ...rest }) => {
    if (hidden) { return <span />; }

    const valParsed = Formatters.float(value, decimalsParse);

    return (
        <FormattedNumber value={valParsed} style="currency" currency="USD" minimumFractionDigits={decimals} maximumFractionDigits={decimals} />
    );
};

export const CheckBox = (value, hidden = false) => {
    if (hidden) { return <span />; }
    return (
        <CheckBox value={value} />
    );
};

export const removeExcessWhitespace = (content) => {
    return content.replace(/\s+/g, ' ');
};

export const Percentage = ({ value, decimals = 2, hidden = false, decimalsParse = 4, ...rest }) => {
    if (hidden) { return <span />; }

    const valParsed = Formatters.float(value, decimalsParse);

    return (
        <FormattedNumber value={valParsed} style="percent" minimumFractionDigits={decimals} maximumFractionDigits={decimals} />
    );
};

export const PrecisionNumber = ({ value, decimals = 2, hidden = false, decimalsParse = 4, ...rest }) => {
    if (hidden) { return <span />; }

    const valParsed = Formatters.float(value, decimalsParse);

    return (
        <FormattedNumber value={valParsed} minimumFractionDigits={decimals} maximumFractionDigits={decimals} />
    );
};

// SECTION HELPERS
export const ContentsSection = ({ title, ...rest }) => {
    return (
        <div>
            <div className="pdf-report-contents-section">
                <span className="pdf-report-contents-section-title">{title}</span>
            </div>
            <div className="pdf-report-contents-section-list">
                {rest.children}
            </div>
        </div>
    );
};

export const ContentsItem = ({ title, pageNum = 0, className = '', sectionName = '', clickHandler, ...rest }) => {
    const pageTitle = (pageNum > 0) ? (
        <a href="#" onClick={clickHandler} className="pdf-contents-link" title={title}>{title}</a>
    ) : (<span>{title}</span>);

    const pageNumber = (pageNum > 0) ? (
        <a href="#" onClick={clickHandler} className="pdf-contents-link" title={`Page ${pageNum}`}>Page {pageNum}</a>
    ) : (<span>Page {pageNum}</span>);

    return (
        <Row className={`pdf-report-component-contents-item ${className}`}>
            <Col xs={8}>
                <ul>
                    <li>{pageTitle}</li>
                </ul>
            </Col>
            <Col xs={3} className="text-right">{pageNumber}</Col>
            <Col xs={1} />
        </Row>
    );
};

export const FundingSection = ({ title, ...rest }) => {
    return (
        <div>
            <div className="pdf-report-funding-section">
                <span className="pdf-report-funding-section-title">{title}</span>
            </div>
            <div className="pdf-report-funding-section-list">
                {rest.children}
            </div>
        </div>
    );
};

export const OffsetData = ({ title = '', className = '', xsOffset = 1, ...rest }) => (
    <Row className={`pdf-report-component-offset-data ${className}`}>
        <Col xs={8 - xsOffset} xsOffset={xsOffset}>{title}</Col>
        <Col xs={12 - (xsOffset + (8 - xsOffset))} className="text-right">
            {rest.children}
        </Col>
    </Row>
);

export const HorizontalDescriptionList = ({ title, value, classNameContainer = 'dl-horizontal', classNameTitle = '', classNameValue = '', ...rest }) => (
    <div className={`hdl ${classNameContainer}`}>
        <div className={`hdl-dt ${classNameTitle}`}>{`${title}${(title) ? ':' : ''}`}</div>
        <div className={`hdl-dd ${classNameValue}`}>{value}{rest.children}</div>
    </div>
);

export const CostExplanation = ({
    explanation,
    classNameContainer = 'dl-horizontal',
    classNameValue = '',
    classNameDiv = 'pdf-page-component-evaluation-cost-explanation',
    ...rest
}) => {
    if (!explanation) { return (<div />); }

    const result = (
        <HorizontalDescriptionList
            title=""
            value=""
            classNameContainer={classNameContainer}
            classNameValue={classNameValue}
        >
            <div className={classNameDiv}>{explanation}</div>
        </HorizontalDescriptionList>
    );

    return result;
};

// 20200127: Improve styling to better match legacy report
export const CoverInfo = ({ title = '', className = '', value, ...rest }) => (
    <Row className={`pdf-report-component-cover-info ${className}`}>
        <Col xs={5}>{title}</Col>
        <Col xs={2}><div /></Col>
        <Col xs={5}>{value}</Col>
    </Row>
);

// 20200127: DEPRECATED FOR: Improve styling to better match legacy report
export const CoverInfoDeprecated = ({ title = '', className = '', value, ...rest }) => (
    <Row className={`pdf-report-component-cover-info ${className}`}>
        <Col xs={6}>{title}</Col>
        <Col xs={6}>{value}</Col>
    </Row>
);

export const CoverCompany = ({ company, className = '', ...rest }) => {
    if (!company || !Formatters.getStr(company, 'name')) { return (<div />); }

    return (
        <div className={`pdf-page-cover-page-company ${className}`}>
            <div>{Formatters.getStr(company, 'name')}</div>
            <div>TEL: {Formatters.getStr(company, 'phone')} | Fax: {Formatters.getStr(company, 'fax')}</div>
            <div>{Formatters.getStr(company, 'url')}</div>
        </div>
    );
};

export const ExecutiveInfo = ({ title = '', className = '', value, ...rest }) => (
    <Row className={`pdf-report-component-executive-info ${className}`}>
        <Col xs={1} />
        <Col xs={6}>{title}</Col>
        <Col xs={2} />
        <Col xs={3}>{value}</Col>
        <Col xs={1} />
    </Row>
);

export const ExecutiveParagraphSection = ({ title = '', body, ...rest }) => (
    <div className="pdf-executive-summary-section">
        <div className="pdf-executive-summary-section-title">{title}</div>
        <div className="pdf-executive-summary-section-body">{body}</div>
        <div className="pdf-executive-summary-section-body">{rest.children}</div>
    </div>
);

export const ParagraphSection = ({ title = '', body, ...rest }) => (
    <div className="pdf-paragraph-section">
        <div className="pdf-paragraph-section-title">{title}</div>
        <div className="pdf-paragraph-section-body">{body}</div>
        <div className="pdf-paragraph-section-body">{rest.children}</div>
    </div>
);

export const GlossarySection = ({ title = '', addDash = true, ...rest }) => {
    const sectionTitle = (title) ? (
        <strong className="pdf-glossary-title">{title}</strong>
    ) : (<span />);

    return (
        <div className="pdf-glossary-section">
            {sectionTitle}{addDash ? ' - ' : ''}{...rest.children}
        </div>
    );
};

// PAGE CONTAINERS
export const ReportPage = ({ wrapperClassName = '', className = '', reportData, ...rest }) => {
    return (
        <div className={`pdf-report-page-wrapper ${wrapperClassName}`}>
            <div className={`pdf-page ${className}`}>{rest.children}</div>
        </div>
    );
};

export const PageHeader = ({ title = '', ...rest }) => {
    const pageTitle = (title) ? (
        <span className="pdf-report-page-header-title">{title}</span>
    ) : (
        <span className="pdf-report-page-header-title">&nbsp;</span>
    );

    if (title) {
        return (
            <div className="pdf-report-page-header text-center" onClick={(e) => { if (rest.scrollToFunc) { rest.scrollToFunc(e, ReportSectionNames.tableOfContents); } }}>{pageTitle}</div>
        );
    } else {
        return (
            <div className="pdf-report-page-header-empty">{pageTitle}</div>
        );
    }
};

export const PageFooter = ({ pageNum = 0, totalPages = 0, footerData, ...rest }) => {
    const { association, branding, currentOrder, currentOrderChart, showFooterFull, showFooterAssocOrderInfo = true, showFooterReportDate = false } = footerData;

    const associationName = (_.get(association, 'name', '')) ? (
        <span>{association.name}</span>
    ) : (<span />);

    // const preparedFYE = (_.get(currentOrder, 'preparedForFYEDate', '')) ? (
    //     <span>Prepared for FYE {shortDate(currentOrder.preparedForFYEDate)}</span>
    // ) : (<span />);    

    const preparedFYE = (_.get(footerData, 'reportPeriodEndDate')) ? (
        <span>Prepared for FYE {shortDate(_.get(footerData, 'reportPeriodEndDate'))}</span>
    ) : (<span />);

    const version = (_.get(currentOrderChart, 'versionId', '')) ? (
        <span>Version {currentOrderChart.versionId}</span>
    ) : (<span />);

    const draft = (_.get(currentOrderChart, 'draft', true) || ReportOverrideDraft) ? (
        <span className="pdf-page-footer-draft">DRAFT</span>
    ) : (<span />);

    const hasPageNumbers = (pageNum > 0 && totalPages > 0);
    const pageNumberResolved = (hasPageNumbers) ? (
        <li><span className="pdf-page-footer-middle-page-number">Page {String(pageNum)} of {String(totalPages)}</span></li>
    ) : (<span />);

    const companyLogo = (hasPageNumbers && _.get(branding, 'logoFooter', '')) ? (
        <img className="pdf-page-footer-logo block-right" alt="Company Logo" src={branding.logoFooter} />
    ) : (<div />);

    // let colLeft = (showFooterFull && showFooterAssocOrderInfo) ? (
    //     <div>
    //         <div className="pdf-page-footer-draft">{draft}</div>
    //         <div className="pdf-page-footer-left-association-name truncate">{associationName}</div>
    //         <div className="pdf-page-footer-left-prepared-for-fye-date">{preparedFYE}</div>
    //         <div className="pdf-page-footer-left-version">{version}</div>
    //     </div>
    // ) : (<div />);

    let colLeft = (<div />);
    if (showFooterFull) {
        if (showFooterReportDate) {
            const reportDate = moment().format('dddd, MMMM D, YYYY');
            colLeft = (
                <div className="pdf-page-footer-middle-container">
                    <div className="pdf-page-footer-left-report-date">{reportDate}</div>
                </div>);
        }

        if (showFooterAssocOrderInfo) {
            colLeft = (
                <div className="pdf-page-footer-left-container">
                    <div className="pdf-page-footer-draft-container">{draft}</div>
                    <div className="pdf-page-footer-left-association-name truncate">{associationName}</div>
                    <div className="pdf-page-footer-left-prepared-for-fye-date">{preparedFYE}</div>
                    <div className="pdf-page-footer-left-version">{version}</div>
                </div>);
        }
    }

    let colMid = (showFooterFull) ? (
        <div className="pdf-page-footer-middle-container">
            <ul className="list-unstyled">
                {pageNumberResolved}
            </ul>
        </div>
    ) : (<div />);

    return (
        <Row className="pdf-page-footer-row" onClick={(e) => { if (rest.scrollToFunc) { rest.scrollToFunc(e, ReportSectionNames.tableOfContents); } }}>
            <Col xs={5} className="pdf-page-footer-left text-left">
                {colLeft}
            </Col>

            <Col xs={2} className="pdf-page-footer-middle text-center">
                {colMid}
            </Col>

            <Col xs={5} className="pdf-page-footer-right text-right">
                <div className="pdf-page-footer-right-company-logo">{companyLogo}</div>
            </Col>
        </Row>
    );
};

export const ReportPageHeaderFooter = ({ title = '', pageNum = 0, totalPageCount = 0, className = '', showFooter = true, footerData, ...rest }) => {
    return (
        <ReportPage className={className} reportData={footerData}>
            <PageHeader title={title} scrollToFunc={rest.scrollToFunc} />

            {rest.children}

            {showFooter ? (<PageFooter scrollToFunc={rest.scrollToFunc} pageNum={pageNum} totalPages={totalPageCount} footerData={footerData} />) : (<div />)}
        </ReportPage>
    );
};

export const ReportPageTableData = ({ theadRow, tbodyRows, pageTitle = '', className = '', classNameTable = '', pageNum = 0, totalPageCount = 0, showFooter = true, footerData, ...rest }) => {
    return (
        <ReportPageHeaderFooter
            className={className}
            footerData={footerData}
            pageNum={pageNum}
            showFooter={showFooter}
            title={pageTitle}
            totalPageCount={totalPageCount}
            scrollToFunc={rest.scrollToFunc}
        >
            <ReportTable theadRow={theadRow} tbodyRows={tbodyRows} classNameTable={classNameTable} />
            {rest.children}
        </ReportPageHeaderFooter>
    );
};

export const ReportTable = ({ theadRow, tbodyRows, className = '', classNameTable = 'pdf-data-table', ...rest }) => {
    return (
        <div className={`report-table-div ${className}`}>
            <Table condensed={true} bordered={true} responsive={false} className={classNameTable}>
                <thead>{theadRow}</thead>
                <tbody>{tbodyRows}</tbody>
            </Table>
        </div>
    );
};

export const ExecutiveSummaryCurrentyProgrammedProjects = ({ inventory, yearlySummaryData, yearSummary, allowOverrides, currentOrder, currentOrderChart, formData, sectionPages, ...rest }) => {
    const projExpenditureData = getExecutiveSummaryExpenditures(inventory, yearlySummaryData, formData.beginYear);
    const projExpYear = _.filter(projExpenditureData, { year: formData.beginYear });
    const projExp = _.reject(projExpYear, { componentId: '' });

    const planExpenditureData = getExecutiveSummaryExpenditures(inventory, yearlySummaryData, 0);
    const planExpYear = _.filter(planExpenditureData, { year: formData.beginYear });
    const planExp = _.reject(planExpYear, { componentId: '' });

    const expendituresCount = _.size(projExp);
    const expendituresCost = _.get(yearSummary, 're', 0);
    let projectsProgrammed = '';

    let expendituresPageNumber = '';
    const pagesExpenditures = _.filter(sectionPages, ['sectionName', ReportSectionNames.projectedReserveExpenditures]);
    if (_.size(pagesExpenditures)) {
        const pageFirst = _.first(pagesExpenditures).pageNumber;
        const pageLast = _.last(pagesExpenditures).pageNumber;

        expendituresPageNumber = `${pageFirst}`;
        if (pageFirst !== pageLast) { expendituresPageNumber += ` - ${pageLast}`; }
    }

    if (_.size(planExp)) {
        const maxProjects = (_.size(planExp) >= 8) ? 6 : _.size(planExp);

        projectsProgrammed = _(planExp)
            .take(maxProjects)
            .map((c, i) => `${_.get(c, 'componentName', '')} (Comp #${getComponentDisplayId(c)})`)
            .value()
            .join('. ');
    }

    let currentlyProgrammedProjects = `There are currently no projects programmed to occur this fiscal year (FY ${currentOrderChart.beginYear})).`;

    if (_.get(currentOrder, 'cppOverride', '') && allowOverrides) {
        return currentOrder.cppOverride;
    }

    if (projectsProgrammed) {
        if (expendituresCount >= 8) {
            currentlyProgrammedProjects = `Projects programmed to occur this fiscal year (FY ${currentOrderChart.beginYear}) include (but are not limited to): ${projectsProgrammed}.
                We have programmed an estimated ${toUSCurrency(expendituresCost)} in reserve expenditures toward
                the completion of those projects programmed to occur in FY ${currentOrderChart.beginYear}. (See Page(s) ${expendituresPageNumber})`;
        } else {
            currentlyProgrammedProjects = `Projects programmed to occur this fiscal year (FY ${currentOrderChart.beginYear}) include: ${projectsProgrammed}.
                We have programmed an estimated ${toUSCurrency(expendituresCost)} in reserve expenditures toward
                the completion of these projects. (See Page(s) ${expendituresPageNumber})`;
        }
    }

    return removeExcessWhitespace(currentlyProgrammedProjects);
};

export const ExecutiveSummarySignificantProjects = ({ inventory, allowOverrides, currentOrder, sectionPages, ...rest }) => {

    if (_.get(currentOrder, 'srpOverride', '') && allowOverrides) {
        return currentOrder.srpOverride;
    }
    const sigProj = getExecutiveSummarySignificantProjects(inventory, 4);

    if (!_.size(sigProj)) {
        return '';
    }

    const sigProjects = _(sigProj)
        .take(4)
        .map((c, i) => `${_.get(c, 'componentName', '')} (Comp #${getComponentDisplayId(c)})`)
        .value()
        .join('. ');

    const fiscalSig = getExecutiveSummaryFiscalSignificance(sigProj, 4);

    let seePages = '';
    const pagesSignificant = _.filter(sectionPages, ['sectionName', ReportSectionNames.significantComponents]);
    if (_.size(pagesSignificant)) {
        const pageFirst = _.first(pagesSignificant).pageNumber;
        const pageLast = _.last(pagesSignificant).pageNumber;

        seePages = `(See Page(s) ${pageFirst})`;

        if (pageFirst !== pageLast) { seePages += ` - ${pageLast}`; }
    }

    return removeExcessWhitespace(`The association's significant reserve projects include: ${sigProjects}. The fiscal significance of these
        components is approximately ${fiscalSig}. A component's significance is calculated by dividing its replacement
        cost by its useful life. In this way, not only is a component's replacement cost considered
        but also the frequency of occurrence. These components most significantly contribute to the total monthly reserve contribution.
        As these components have a high level of fiscal significance the association should properly maintain them to ensure
        they reach their full useful lives. ${seePages}`);
};

export const SPECIAL_ASSESSMENT_CLIENT_NOTE = '* Requested by the client; outside baseline recommendations.';

export const getSpecialAssessmentSchedulePageReference = (sectionPages: any[]): string => {
    const pages = _.filter(sectionPages, ['sectionName', ReportSectionNames.specialAssessments]);
    if (!_.size(pages)) {
        return '';
    }
    const pageFirst = _.first(pages).pageNumber;
    const pageLast = _.last(pages).pageNumber;
    return `(See ${(pageFirst !== pageLast) ? `Pages` : `Page`} ${pageFirst}${(pageFirst !== pageLast) ? ` - ${pageLast}` : ''})`;
};

export const buildSpecialAssessmentSummary = (presentation: ISpecialAssessmentPresentation, variant: 'executive' | 'funding', sectionPages: any[] = []) => {
    const renderLine = (key: string, title: string, amount: number) => (variant === 'executive')
        ? (<ExecutiveInfo key={key} className="pdf-special-assessment-line" title={title} value={<Currency value={amount} decimals={0} />} />)
        : (<OffsetData key={key} className="pdf-special-assessment-line" title={title}><Currency value={amount} decimals={0} /></OffsetData>);

    const renderNote = (key: string) => (<div key={key} className="pdf-special-assessment-note">{SPECIAL_ASSESSMENT_CLIENT_NOTE}</div>);
    const hasClient = presentation.clientRows.length > 0;

    // Client rows end with an asterisk, explained by a small footnote.
    const buildAsterisk = () => {
        const lines: any[] = [];
        if (presentation.showInline) {
            _.forEach(presentation.rows, (row) => lines.push(renderLine(row.id, row.clientRequested ? `${row.title} *` : row.title, row.amount)));
        } else {
            lines.push(renderLine('company-total', `Special Assessments (${presentation.companyRows.length})`, presentation.companyTotal));
            if (hasClient) {
                lines.push(renderLine('client-total', `Special Assessments (${presentation.clientRows.length}) *`, presentation.clientTotal));
            }
        }
        if (hasClient) {
            lines.push(renderNote('client-note'));
        }
        const pageReference = presentation.showInline ? '' : getSpecialAssessmentSchedulePageReference(sectionPages);
        if (pageReference) {
            lines.push(<div key="schedule-reference" className="pdf-special-assessment-note">{pageReference}</div>);
        }
        return lines;
    };

    return buildAsterisk();
};

export const ExecutiveSummaryReserveFunding = ({ begBalance, idealBalance, percentFunded, percentageFunded, showMinimum, numberUnits, allowOverrides, currentOrder, currentOrderChart, specialAssessmentRows = [] as any[], sectionPages, ...rest }) => {
    if (currentOrder.rfOverride && allowOverrides) {
        return currentOrder.rfOverride;
    }
    let msg =
        `In comparing the projected starting reserve balance of ${toUSCurrency(begBalance)} versus \
        the ideal reserve balance of ${toUSCurrency(idealBalance)} we find the association\'s \
        reserve fund to be approximately ${toPercentage(percentageFunded)} funded. `;

    let msgPercentFunded = '';
    let msgRecMrcPrefix = '';
    if (percentFunded <= 0) {
        msgPercentFunded = 'This indicates the association does not currently have a fund position.';
        msgRecMrcPrefix = 'In order to strengthen the account fund, ';
    } else if (_.inRange(percentFunded, 1, 31)) {
        msgPercentFunded = 'This indicates a relatively weak reserve fund position. ';
        msgRecMrcPrefix = 'In order to strengthen the account fund, ';
    } else if (_.inRange(percentFunded, 31, 70)) {
        msgPercentFunded = 'This indicates a fair reserve fund position. ';
        msgRecMrcPrefix = 'In order to continue to strengthen the account fund, ';
    } else if (_.inRange(percentFunded, 70, 100)) {
        msgPercentFunded = 'This indicates a strong reserve fund position. ';
        msgRecMrcPrefix = 'In order to continue to strengthen the account fund, ';
    } else if (percentFunded >= 100) {
        msgPercentFunded = 'This indicates a fully funded reserve fund position. ';
        msgRecMrcPrefix = 'In order to maintain the account fund, ';
    }

    // Recommendation language uses company-recommended rows only, already ordered by year.
    const numSpecAsessments = _.size(specialAssessmentRows);
    const specAsessYears = _.map(specialAssessmentRows, (entryObj) => entryObj.year);
    const specAssessYearsString = specAsessYears.join(', ');
    const specAssessFirstYearAmount = _.get(_.first(specialAssessmentRows), 'amount', 0);

    let msgSA = '';
    if (numSpecAsessments > 0) {
        if (numSpecAsessments > 0) { // year.and needs to become year. As well as ... along with a ternary to determine if there are extra years (and the updated language from Rob's email)
            if (numSpecAsessments > 1) {
                msgSA = `Due to the currently programmed short-term reserve expenses, this indicates a relatively weak reserve fund position. Therefore we are recommending a one-time special assessment \
                 of ${Formatters.formatCurrencyIntl(specAssessFirstYearAmount, 0)} in the upcoming year to pay for these programmed reserve expenses. \
                 As well as an additional (${numSpecAsessments}) assessments in ${specAssessYearsString}. \
                 Note that a special assessment can be increased/decreased based on the actual cost of these projects.`;
            } else {
                msgSA = `Due to the currently programmed short-term reserve expenses, this indicates a relatively weak reserve fund position. Therefore we are recommending a one-time special assessment \
                of ${Formatters.formatCurrencyIntl(specAssessFirstYearAmount, 0)} in the upcoming year to pay for these programmed reserve expenses.`;
            }
        }
    }

    const recMrcPerUnit = (currentOrderChart.recommendedMRCVal > 0 && numberUnits > 0) ? currentOrderChart.recommendedMRCVal / numberUnits : 0;
    const minMrcPerUnit = (currentOrderChart.minimumMRCVal > 0 && numberUnits > 0) ? currentOrderChart.minimumMRCVal / numberUnits : 0;
    const hasUnits = (numberUnits > 0);
    const hideUnits = _.get(currentOrderChart, 'hideNumberOfUnits', false);
    const msgRecMrcPerUnit = (hideUnits) ? '' : (hasUnits) ? `(${toUSCurrency(recMrcPerUnit)}/unit)` : '';
    const msgMinMrcPerUnit = (hideUnits) ? '' : (hasUnits) ? `(${toUSCurrency(minMrcPerUnit)}/unit)` : '';

    const msgRecMrc =
        `we suggest adopting a monthly reserve contribution
            of ${toUSCurrency(currentOrderChart.recommendedMRCVal)} ${msgRecMrcPerUnit}
            per month. `;

    const msgRecMrcMinMrc = (showMinimum) ?
        `For comparison purposes, we have also set a minimum reserve contribution
            of ${toUSCurrency(currentOrderChart.minimumMRCVal)} ${msgMinMrcPerUnit}
            per month. ` : '';

    const msgRecMrcEnd = (
        `If the contribution falls below this rate, then the reserve fund may fall into a situation where ${(numSpecAsessments > 0) ? 'additional' : ''} special assessments, \
            deferred maintenance, and lower property values are likely at some point in the future.`
    );
    return removeExcessWhitespace([msg, msgPercentFunded, msgSA, msgRecMrcPrefix, msgRecMrc, msgRecMrcMinMrc, msgRecMrcEnd].join(' '));
};

export const WarningAlert = ({ title, body, ...rest }) => {
    let result = (<div />);

    result = (
        <div className="alert-general">
            <Alert bsStyle="warning">
                <h4><i className="fa fa-exclamation-triangle" /> {title}</h4>
                <p>{body}</p>
            </Alert>
        </div>);

    return result;
};

// HELPER FUNCTIONS
export const getRowKey = (id: any, sectionName: string, tableName: string = 'tbl') => {
    if (!id || !sectionName || !tableName) { return `rand-${new Date().getTime()}-${_.random(1, 9999999)}`; }

    return `${sectionName}-table-${tableName}-id-${id}`;
};

export const withPageRegistration = (
    Component,
    sectionName: string,
    registerFunc?: (s: string, p: string, i: number, inc: boolean) => number,
    refFunc?: (e: any, s: string, i: number) => void,
    scrollToFunc?: (e: any, s: string) => void,
    excludeFromPageCount?: any
) => (props) => {
    let { pageNum, className, totalPageCount, pageIndex, children, ...rest } = props;

    let pageNumber: number = pageNum;
    const include = (excludeFromPageCount) ? false : true;

    if (registerFunc) {
        pageNumber = registerFunc(sectionName, props.title, pageIndex, include);
    }

    return (
        <div ref={(e) => { if (refFunc) { refFunc(e, sectionName, pageIndex); } }} className="withPageRegistration-component-wrapper">
            <Component
                className={className}
                pageNum={pageNumber}
                totalPageCount={totalPageCount}
                scrollToFunc={scrollToFunc}
                {...rest}
            >
                {...children}
            </Component>
        </div>
    );
};

export const getExecutiveSummaryExpenditures = (inventory: any[], dataItems: any[], beginYear: number) => {
    const eligibleExpenditures = _(inventory)
        .filter(i => {
            let rul = _.toSafeInteger(_.get(i, 'remainingUsefulLife', 0));
            let ul = _.get(i, 'usefulLife', '');

            if (
                (rul === beginYear || rul === 0 && _.toSafeInteger(ul) === beginYear) &&
                (AppUtilities.isNumeric(ul) || ul === AppDataConstants.UsefulLifeOptions.OneTimeExpense) &&
                (i.bestCost > 0)
            ) {
                return true;
            }

            return false;
        })
        .flatMap('expenditureMap')
        .filter(o => {
            return (o.hypoProjectedExpenseShare > 0) && (o.hypoRemainingLife === 0);
        })
        .value();

    const transformed = _(dataItems)
        .map((yearData, yearInd) => {
            const curYear = _.get(yearData, 'year', 0);

            const projectedExpenditures = (yearData.re > 0) ? _(eligibleExpenditures)
                .filter({ 'year': curYear })
                .map((projectedExpense, projectedExpenseInd) => {
                    const expenseItem = _(inventory)
                        .find({ 'inventoryId': _.get(projectedExpense, 'inventoryId', 0) });

                    const componentSortFields = getComponentSortFields(expenseItem, projectedExpenseInd);

                    const expenseResult = {
                        componentDecimal: _.get(expenseItem, 'componentDecimal'),
                        componentId: _.get(expenseItem, 'componentId', 0),
                        componentName: _.get(expenseItem, 'componentName', 0),
                        inventoryId: _.get(expenseItem, 'inventoryId', 0),
                        projectedCost: _.get(projectedExpense, 'projectedExpenseShare'),
                        __componentSortBase: componentSortFields.__componentSortBase,
                        __componentSortDecimal: componentSortFields.__componentSortDecimal,
                        __componentSortStable: componentSortFields.__componentSortStable,
                    };

                    return expenseResult;
                })
                .orderBy(['__componentSortBase', '__componentSortDecimal', 'componentName', '__componentSortStable'], ['asc', 'asc', 'asc', 'asc'])
                .value() : [];

            const totalPerAnnum = _.sumBy(projectedExpenditures, 'projectedCost');
            const firstItem = _.head(projectedExpenditures);

            const yearRecord = _.merge(
                {},
                {
                    componentDecimal: _.get(firstItem, 'componentDecimal'),
                    componentId: '',
                    componentName: 'No Expenditures Projected',
                    projectedCost: 0,
                    showTotalPerAnnum: false,
                    totalPerAnnum: totalPerAnnum,
                    year: yearData.year,
                },
                firstItem
            );

            const yearChildRecords = _(projectedExpenditures)
                .tail()
                .map((item, itemInd) => {
                    return {
                        componentDecimal: _.get(item, 'componentDecimal'),
                        componentId: _.get(item, 'componentId', ''),
                        componentName: _.get(item, 'componentName', ''),
                        projectedCost: _.get(item, 'projectedCost', 0),
                        showTotalPerAnnum: false,
                        totalPerAnnum: totalPerAnnum,
                        year: yearData.year, // Was '' empty string???
                    };
                })
                .value();

            const yearResults = _(yearRecord)
                .concat(yearChildRecords)
                .reverse()
                .set('[0].showTotalPerAnnum', true)
                .set('[0].className', 'pdf-data-table-projected-reserve-expenditures-year-row')
                .reverse()
                .value();

            return yearResults;
        })
        .flatten()
        .value();

    return transformed;
};

export const getExecutiveSummarySignificantProjects = (inventory: any[], topComponentCount: number = 4) => {
    const sumAvgCostsPerUL = _.sumBy(inventory, 'expenditureMap[0].signAsDollar');
    const data = _(inventory)
        .orderBy('componentId')
        .map((inv, i) => {
            const invExpenditure = _.get(inv, 'expenditureMap[0]');

            const avgCost = _.get(inv, 'avgCost_sort', 0);
            const idealBal = _.get(invExpenditure, 'hypoProjectedExpenseShare', 0);
            const curBal = _.get(invExpenditure, 'projectedExpenseShare', 0);
            const usefulLife = _.get(inv, 'usefulLife_sort', 0);
            const usefulLifeVal = _.get(inv, 'usefulLife', '');
            const avgCostPerUL = _.get(invExpenditure, 'signAsDollar', 0); // signasdollar

            let percent = 0;
            if (avgCostPerUL > 0 && sumAvgCostsPerUL > 0) {
                percent = (avgCostPerUL / sumAvgCostsPerUL);
            }

            let usefulLifeText = usefulLife;
            if (usefulLife === 0) {
                let newUsefulLife = usefulLifeVal;

                if (usefulLifeVal !== AppDataConstants.UsefulLifeOptions.OneTimeExpense) {
                    newUsefulLife = AppDataConstants.UsefulLifeOptions.NotApplicable;
                }

                // console.log(`report-components getExecutiveSummarySignificantProjects inventoryId ${_.get(inv, 'inventoryId', '-9999')} usefulLife ${usefulLife} usefulLifeVal ${usefulLifeVal} newUsefulLife ${newUsefulLife}`);

                usefulLifeText = newUsefulLife;
            }

            return {
                componentDecimal: inv.componentDecimal,
                componentId: inv.componentId,
                componentName: inv.componentName,
                usefulLife: usefulLifeText, // (inv.usefulLife === 0) ? 'N/A' : inv.usefulLife,
                remainingUsefulLife: inv.remainingUsefulLife,
                quantity: inv.quantity,
                avgCost: avgCost,
                avgCostPerUL: avgCostPerUL,
                idealBal: idealBal,
                curBal: curBal,
                percent: percent,
            };
        })
        .value();

    const rowData = _.orderBy(data, 'percent', ['desc']);
    let components = _.slice(rowData, 0, topComponentCount);

    return components;
};

export const getExecutiveSummaryFiscalSignificance = (projects: any[], limit: number = 4) => {
    if (!_.size(projects)) { return ''; }
    const limitClamped = _.clamp(limit, 1, _.size(projects));

    const result = _(projects)
        .take(limitClamped)
        .map((c, i, a) => {
            const p = `${Math.round(_.get(c, 'percent', 0) * 100)}%`;

            if (_.size(a) <= 1) {
                return `${p}`;
            } else {
                if (i === (_.size(a) - 2)) {
                    // Next to last
                    return `${p} and`;
                } else if (i === (_.size(a) - 1)) {
                    // Last
                    return `${p} respectively`;
                } else {
                    return `${p},`;
                }
            }
        })
        .value()
        .join(' ');

    // console.log(`getFiscalSignificance projects.len ${_.size(projects)} limit ${limit} limitClamped ${limitClamped} result: ${result}`, projects);

    return result;
};



// WEBPACK FOOTER //
// ./src/report/pdf/report-components.tsx